import type { RoomDocument } from '../api/types';

/**
 * Cadence for re-polling a room's documents while processing is still
 * resolving (classification is async: the finalize 201 is always `processing`,
 * a background job settles it seconds later; then OCR / the redaction check).
 *
 * Fast while the room is MOVING, backing off once it stops moving, giving up
 * after {@link GIVE_UP_AFTER_MS} without progress, and stopping at once on an
 * error. "Moving" = since the last poll a document started processing, changed
 * step, or settled. A big batch (hundreds of files classified one every few
 * seconds) therefore keeps the fast cadence for as long as it keeps settling —
 * the age used to be measured from when the newest document was first seen, so
 * a long batch decayed to a 15 s poll and then stopped while documents were
 * still being filed (owner report 2026-10-12: "none of that status is reflected
 * on the upload page"). One stuck document (a dead job) still stops the poll
 * once nothing else moves for 10 minutes. A stopped poll resumes on the next
 * refetch that sees movement (any invalidation, window focus, a new upload).
 */
export const STEPS: readonly { untilMs: number; everyMs: number }[] = [
  { untilMs: 30_000, everyMs: 2_000 },
  { untilMs: 120_000, everyMs: 5_000 },
  { untilMs: 600_000, everyMs: 15_000 },
];
export const GIVE_UP_AFTER_MS = 600_000;

type PolledDocument = Pick<
  RoomDocument,
  'id' | 'classificationStatus' | 'processingStatus' | 'ocrStatus' | 'ocrProgress' | 'redactionStatus'
>;

/** Still being worked on server-side: classification running, a scan being
 * read (`ocr`), the personal-data redaction check queued/running — or the
 * background OCR / redaction a FILED document still shows as pending. Those run
 * after filing without holding `processingStatus` (it reads `done` while the
 * file's "OCR pending" / "Redaction pending" indicators are still on), so
 * without them the poll stopped and the indicators never turned "complete". */
export function isDocumentInFlight(
  d: Pick<RoomDocument, 'classificationStatus' | 'processingStatus'> &
    Partial<Pick<RoomDocument, 'ocrStatus' | 'redactionStatus'>>,
): boolean {
  return (
    d.classificationStatus === 'processing' ||
    d.processingStatus === 'processing' ||
    d.processingStatus === 'ocr' ||
    d.processingStatus === 'redaction_check' ||
    d.ocrStatus === 'pending' ||
    d.redactionStatus === 'pending'
  );
}

/** What the poll remembers between calls (one per documents query, module-level). */
export interface DocumentsPollTracker {
  /** Each in-flight document → the step it was last seen in. */
  steps: Map<string, string>;
  /** When the in-flight set last moved; null before the first poll. */
  lastProgressAt: number | null;
}

export function documentsPollTracker(): DocumentsPollTracker {
  return { steps: new Map(), lastProgressAt: null };
}

/**
 * The delay before the next poll, or `false` to stop. `tracker` is updated in
 * place: the in-flight documents and their steps, and when that last changed.
 */
export function nextDocumentsPoll(
  docs: PolledDocument[] | undefined,
  tracker: DocumentsPollTracker,
  now: number,
  errored: boolean,
): number | false {
  const current = new Map<string, string>();
  for (const d of docs ?? []) {
    if (isDocumentInFlight(d)) {
      // Every visible step counts as movement, OCR page progress included.
      current.set(
        d.id,
        [
          d.classificationStatus,
          d.processingStatus,
          d.ocrStatus,
          d.ocrProgress?.pagesDone,
          d.redactionStatus,
        ].join('/'),
      );
    }
  }
  let moved = tracker.lastProgressAt === null || current.size !== tracker.steps.size;
  if (!moved) {
    for (const [id, step] of current) {
      if (tracker.steps.get(id) !== step) {
        moved = true;
        break;
      }
    }
  }
  tracker.steps = current;
  if (moved) tracker.lastProgressAt = now;

  if (errored || current.size === 0) return false;
  const age = now - (tracker.lastProgressAt ?? now);
  return STEPS.find((s) => age < s.untilMs)?.everyMs ?? false;
}
